import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Play, Pause, SkipBack, SkipForward, Repeat, Shuffle, Search, Repeat1 } from 'lucide-react';

const ReciterMusicLibrary = () => {
  const { reciterId } = useParams();
  const navigate = useNavigate();
  
  const [reciter, setReciter] = useState(null);
  const [recitations, setRecitations] = useState([]);
  const [surahs, setSurahs] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Player state
  const [currentIndex, setCurrentIndex] = useState(-1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [autoNext, setAutoNext] = useState(true);
  const [repeat, setRepeat] = useState(false);
  const [shuffle, setShuffle] = useState(false);
  
  // Like state
  const [likeCount, setLikeCount] = useState(0);
  const [hasLikedToday, setHasLikedToday] = useState(false);
  const [likeLoading, setLikeLoading] = useState(false);
  
  // Audio error state
  const [audioError, setAudioError] = useState(null);
  
  // Simulation timer for when audio doesn't work
  const simulationTimerRef = useRef(null);
  const isSimulatingRef = useRef(false);
  
  // Refs for stable callbacks
  const audioRef = useRef(null);
  const pendingPlayRef = useRef(false);
  const shuffleRef = useRef(shuffle);
  const repeatRef = useRef(repeat);
  const autoNextRef = useRef(autoNext);
  const recitationsRef = useRef(recitations);
  const currentIndexRef = useRef(currentIndex);
  
  // Keep refs in sync
  useEffect(() => { shuffleRef.current = shuffle; }, [shuffle]);
  useEffect(() => { repeatRef.current = repeat; }, [repeat]);
  useEffect(() => { autoNextRef.current = autoNext; }, [autoNext]);
  useEffect(() => { recitationsRef.current = recitations; }, [recitations]);
  useEffect(() => { currentIndexRef.current = currentIndex; }, [currentIndex]);
  
  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch reciter
        const reciterRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/reciters/${reciterId}`);
        const reciterData = await reciterRes.json();
        setReciter(reciterData);
        
        // Fetch all surahs
        const surahsRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/surahs`);
        const surahsData = await surahsRes.json();
        setSurahs(surahsData);
        
        // Fetch all recitations for this reciter
        const recitationsRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/recitations/reciter/${reciterId}`);
        const recitationsData = await recitationsRes.json();
        console.log('[ReciterMusicLibrary] Recitations loaded:', recitationsData.length);
        
        // Sort by order
        const sortedRecitations = recitationsData.sort((a, b) => a.order - b.order);
        setRecitations(sortedRecitations);
        
        // Fetch like count
        try {
          const likesRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/reciters/${reciterId}/likes`);
          const likesData = await likesRes.json();
          setLikeCount(likesData.likeCount || 0);
        } catch (e) {
          console.log('Could not fetch likes:', e);
        }
        
        // Check if already liked today (from localStorage)
        const today = new Date().toISOString().split('T')[0];
        const likeKey = `liked_${reciterId}_${today}`;
        if (localStorage.getItem(likeKey)) {
          setHasLikedToday(true);
        }
        
        setLoading(false);
        
        // Don't auto-load, let user click play
        console.log('[ReciterMusicLibrary] Data loaded, ready for user interaction');
      } catch (error) {
        console.error('Error fetching data:', error);
        setLoading(false);
      }
    };
    
    fetchData();
  }, [reciterId]);
  
  // Get surah name
  const getSurahName = (surahId) => {
    const surah = surahs.find(s => s.id === surahId);
    return surah ? surah.name : 'Bilinmeyen Sure';
  };
  
  const getSurahArabic = (surahId) => {
    const surah = surahs.find(s => s.id === surahId);
    return surah ? surah.nameArabic : '';
  };
  
  // Like reciter function - toggle like/unlike
  const handleLike = async () => {
    if (likeLoading) return;
    
    setLikeLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/reciters/${reciterId}/like`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      const data = await response.json();
      
      if (data.success) {
        setLikeCount(data.likeCount);
        setHasLikedToday(data.liked);
        // Update localStorage
        const today = new Date().toISOString().split('T')[0];
        const likeKey = `liked_${reciterId}_${today}`;
        if (data.liked) {
          localStorage.setItem(likeKey, 'true');
        } else {
          localStorage.removeItem(likeKey);
        }
      }
    } catch (error) {
      console.error('Like error:', error);
    } finally {
      setLikeLoading(false);
    }
  };

  const getSurahVerseCount = (surahId) => {
    const surah = surahs.find(s => s.id === surahId);
    return surah ? surah.verseCount : 0;
  };
  
  // Get audio URL
  const getAudioUrl = useCallback((audioPath) => {
    if (!audioPath) {
      console.warn('[ReciterMusicLibrary] No audio path provided');
      return '';
    }
    if (audioPath.startsWith('http')) return audioPath;
    const url = `${process.env.REACT_APP_BACKEND_URL}${audioPath.replace('/uploads/', '/api/files/')}`;
    return url;
  }, []);
  
  // Calculate total duration
  const getTotalDuration = () => {
    const totalSeconds = recitations.reduce((sum, rec) => {
      const parts = rec.duration?.split(':') || [];
      if (parts.length === 3) {
        return sum + (parseInt(parts[0]) * 3600) + (parseInt(parts[1]) * 60) + parseInt(parts[2]);
      } else if (parts.length === 2) {
        return sum + (parseInt(parts[0]) * 60) + parseInt(parts[1]);
      }
      return sum;
    }, 0);
    
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    return `${hours} saat ${minutes} dk`;
  };
  
  // Core play function - handles actual audio play with retries
  const playAudio = useCallback(async () => {
    if (!audioRef.current) return false;
    
    const maxRetries = 3;
    for (let i = 0; i < maxRetries; i++) {
      try {
        // Wait for audio to be ready
        if (audioRef.current.readyState < 2) {
          await new Promise(resolve => {
            const handler = () => {
              audioRef.current.removeEventListener('canplay', handler);
              resolve();
            };
            audioRef.current.addEventListener('canplay', handler);
            // Timeout fallback
            setTimeout(resolve, 300);
          });
        }
        
        await audioRef.current.play();
        console.log('[ReciterMusicLibrary] ✅ Audio playing successfully');
        return true;
      } catch (err) {
        if (err.name === 'AbortError') {
          // Play was interrupted, wait and retry
          await new Promise(r => setTimeout(r, 100));
          continue;
        } else if (err.name === 'NotAllowedError') {
          console.log('[ReciterMusicLibrary] ⚠️ Autoplay blocked by browser');
          return false;
        }
        console.error('[ReciterMusicLibrary] Play error attempt', i + 1, err);
      }
    }
    return false;
  }, []);
  
  // Load and play a track
  const loadAndPlayTrack = useCallback(async (index) => {
    if (!audioRef.current || !recitationsRef.current[index]) {
      console.log('[ReciterMusicLibrary] Cannot load - no audio ref or recitation');
      return;
    }
    
    const recitation = recitationsRef.current[index];
    const url = getAudioUrl(recitation.audioUrl);
    
    console.log('[ReciterMusicLibrary] 🎵 Loading track:', index, 'URL:', url, 'audioUrl:', recitation.audioUrl);
    
    if (!url) {
      console.error('[ReciterMusicLibrary] No audio URL for recitation');
      return;
    }
    
    // Clear previous error
    setAudioError(null);
    
    // Pause current playback
    audioRef.current.pause();
    
    // Set new source
    audioRef.current.src = url;
    audioRef.current.currentTime = 0;
    setCurrentTime(0);
    
    // Wait for the audio to be ready
    try {
      await new Promise((resolve, reject) => {
        const onCanPlay = () => {
          console.log('[ReciterMusicLibrary] ✅ canplay event fired');
          cleanup();
          resolve();
        };
        const onLoadedData = () => {
          console.log('[ReciterMusicLibrary] ✅ loadeddata event fired');
          cleanup();
          resolve();
        };
        const onError = (e) => {
          const error = audioRef.current?.error;
          console.error('[ReciterMusicLibrary] ❌ Audio error event:', error?.code, error?.message);
          cleanup();
          // Check if it's a format error
          if (error?.code === 4) {
            setAudioError('Bu ses dosyası formatı (M4A) tarayıcınızda desteklenmiyor. Lütfen MP3 formatında dosya yükleyin.');
          }
          reject(new Error(`Audio load error: ${error?.message || 'Unknown'}`));
        };
        
        const cleanup = () => {
          audioRef.current?.removeEventListener('canplay', onCanPlay);
          audioRef.current?.removeEventListener('loadeddata', onLoadedData);
          audioRef.current?.removeEventListener('error', onError);
        };
        
        audioRef.current.addEventListener('canplay', onCanPlay);
        audioRef.current.addEventListener('loadeddata', onLoadedData);
        audioRef.current.addEventListener('error', onError);
        
        // Timeout fallback - resolve anyway after 3 seconds
        setTimeout(() => {
          console.log('[ReciterMusicLibrary] ⏱️ Timeout reached, trying to play anyway');
          cleanup();
          resolve();
        }, 3000);
        
        audioRef.current.load();
      });
      
      console.log('[ReciterMusicLibrary] ✅ Audio ready, attempting to play...');
      
      // Stop any existing simulation
      if (simulationTimerRef.current) {
        clearInterval(simulationTimerRef.current);
        simulationTimerRef.current = null;
      }
      isSimulatingRef.current = false;
      
      // Now try to play
      await audioRef.current.play();
      setIsPlaying(true);
      setAudioError(null);
      console.log('[ReciterMusicLibrary] ✅ Audio playing successfully');
      incrementListenCount(recitation.id);
    } catch (err) {
      console.error('[ReciterMusicLibrary] Load/Play error:', err.message || err.name, err);
      
      // Set error message for UI
      const isFormatError = err.message?.includes('no supported streams') || 
                           err.message?.includes('DEMUXER_ERROR') ||
                           err.name === 'NotSupportedError';
      
      if (isFormatError) {
        setAudioError('Bu ses dosyası formatı tarayıcınızda desteklenmiyor. Lütfen MP3 formatında dosya yükleyin.');
        
        // Start simulation mode - use duration from recitation data
        const recDuration = recitation.duration;
        if (recDuration) {
          const parts = recDuration.split(':');
          let totalSeconds = 0;
          if (parts.length === 3) {
            totalSeconds = parseInt(parts[0]) * 3600 + parseInt(parts[1]) * 60 + parseInt(parts[2]);
          } else if (parts.length === 2) {
            totalSeconds = parseInt(parts[0]) * 60 + parseInt(parts[1]);
          }
          
          if (totalSeconds > 0) {
            setIsPlaying(true);
            setDuration(totalSeconds);
            
            // Start simulation timer
            isSimulatingRef.current = true;
            let simulatedTime = 0;
            console.log('[ReciterMusicLibrary] 🎭 Starting simulation mode, duration:', totalSeconds);
            
            simulationTimerRef.current = setInterval(() => {
              simulatedTime += 1;
              setCurrentTime(simulatedTime);
              
              // Check if track ended
              if (simulatedTime >= totalSeconds) {
                console.log('[ReciterMusicLibrary] 🎭 Simulation ended, moving to next track');
                clearInterval(simulationTimerRef.current);
                simulationTimerRef.current = null;
                isSimulatingRef.current = false;
                
                // Trigger next track if autoNext is enabled
                if (autoNextRef.current) {
                  // Use setTimeout to break out of the interval callback
                  setTimeout(() => {
                    const currentIdx = currentIndexRef.current;
                    const currentRecitations = recitationsRef.current;
                    
                    if (shuffleRef.current && currentRecitations.length > 1) {
                      let randomIndex;
                      do {
                        randomIndex = Math.floor(Math.random() * currentRecitations.length);
                      } while (randomIndex === currentIdx);
                      setCurrentIndex(randomIndex);
                    } else if (currentIdx < currentRecitations.length - 1) {
                      setCurrentIndex(currentIdx + 1);
                    } else if (repeatRef.current) {
                      setCurrentIndex(0);
                    } else {
                      setIsPlaying(false);
                    }
                  }, 100);
                } else {
                  setIsPlaying(false);
                }
              }
            }, 1000);
          }
        }
      } else {
        setIsPlaying(false);
      }
    }
  }, [getAudioUrl]);
  
  // Toggle play/pause
  const togglePlay = useCallback(async () => {
    if (!audioRef.current) return;
    
    try {
      if (!audioRef.current.paused || isSimulatingRef.current) {
        audioRef.current.pause();
        // Stop simulation if running
        if (simulationTimerRef.current) {
          clearInterval(simulationTimerRef.current);
          simulationTimerRef.current = null;
        }
        isSimulatingRef.current = false;
        setIsPlaying(false);
      } else {
        await audioRef.current.play();
        setIsPlaying(true);
      }
    } catch (error) {
      if (error.name !== 'AbortError' && error.name !== 'NotAllowedError') {
        console.error('[ReciterMusicLibrary] Toggle error:', error);
      }
      setIsPlaying(false);
    }
  }, []);
  
  // Play specific track - FIXED: Clean implementation
  const playTrack = useCallback(async (index) => {
    console.log('[ReciterMusicLibrary] 🎯 playTrack called:', index, 'current:', currentIndexRef.current, 'recitations:', recitationsRef.current.length);
    
    // Check if recitations exist
    if (!recitationsRef.current || recitationsRef.current.length === 0) {
      console.log('[ReciterMusicLibrary] ⚠️ No recitations available yet');
      return;
    }
    
    // Same track - toggle play/pause
    if (index === currentIndexRef.current && audioRef.current?.src) {
      console.log('[ReciterMusicLibrary] Same track - toggling');
      togglePlay();
      return;
    }
    
    // Mark that we want to play
    pendingPlayRef.current = true;
    
    // Update index - this will trigger the useEffect
    setCurrentIndex(index);
  }, [togglePlay]);
  
  // Next track - FIXED: Use refs to get current state values
  const nextTrack = useCallback(() => {
    const currentShuffle = shuffleRef.current;
    const currentRepeat = repeatRef.current;
    const currentRecitations = recitationsRef.current;
    const currentIdx = currentIndexRef.current;
    
    console.log('[ReciterMusicLibrary] ⏭️ Next track called - shuffle:', currentShuffle, 'repeat:', currentRepeat, 'currentIdx:', currentIdx, 'recitations:', currentRecitations.length);
    
    // Check if recitations exist
    if (!currentRecitations || currentRecitations.length === 0) {
      console.log('[ReciterMusicLibrary] ⚠️ No recitations available for next track');
      return;
    }
    
    pendingPlayRef.current = true;
    
    if (currentShuffle && currentRecitations.length > 1) {
      // Pick a random track different from current
      let randomIndex;
      do {
        randomIndex = Math.floor(Math.random() * currentRecitations.length);
      } while (randomIndex === currentIdx);
      
      console.log('[ReciterMusicLibrary] 🔀 Shuffle: jumping to index', randomIndex);
      setCurrentIndex(randomIndex);
    } else if (currentIdx < currentRecitations.length - 1) {
      console.log('[ReciterMusicLibrary] ➡️ Sequential: next track', currentIdx + 1);
      setCurrentIndex(currentIdx + 1);
    } else if (currentRepeat) {
      console.log('[ReciterMusicLibrary] 🔁 Repeat: back to start');
      setCurrentIndex(0);
    } else {
      console.log('[ReciterMusicLibrary] ⏹️ End of playlist');
      pendingPlayRef.current = false;
      setIsPlaying(false);
    }
  }, []);
  
  // Previous track
  const previousTrack = useCallback(() => {
    const currentIdx = currentIndexRef.current;
    console.log('[ReciterMusicLibrary] ⏮️ Previous track from', currentIdx);
    
    if (currentIdx > 0) {
      pendingPlayRef.current = true;
      setCurrentIndex(currentIdx - 1);
    }
  }, []);
  
  // Cleanup simulation on unmount
  useEffect(() => {
    return () => {
      if (simulationTimerRef.current) {
        clearInterval(simulationTimerRef.current);
      }
    };
  }, []);
  
  // Audio events - FIXED: Stable event handlers with refs
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    
    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
      // Update duration if available
      if (audio.duration && !isNaN(audio.duration)) {
        setDuration(audio.duration);
      }
    };
    
    const handleLoadedMetadata = () => {
      console.log('[ReciterMusicLibrary] 📊 Metadata loaded, duration:', audio.duration);
      if (audio.duration && !isNaN(audio.duration)) {
        setDuration(audio.duration);
      }
    };
    
    const handleEnded = () => {
      console.log('[ReciterMusicLibrary] 🔚 Track ended - autoNext:', autoNextRef.current);
      if (autoNextRef.current) {
        nextTrack();
      } else {
        setIsPlaying(false);
      }
    };
    
    const handlePlay = () => {
      setIsPlaying(true);
    };
    
    const handlePause = () => {
      setIsPlaying(false);
    };
    
    const handleError = (e) => {
      console.error('[ReciterMusicLibrary] Audio error:', e);
      setIsPlaying(false);
    };
    
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('error', handleError);
    
    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('error', handleError);
    };
  }, [nextTrack]);
  
  // Track change effect - FIXED: Load and play when index changes
  useEffect(() => {
    console.log('[ReciterMusicLibrary] 🔄 Track effect triggered - index:', currentIndex, 'recitations length:', recitations.length);
    
    if (currentIndex < 0) {
      console.log('[ReciterMusicLibrary] ⏭️ Skipping - negative index');
      return;
    }
    
    if (!recitations[currentIndex]) {
      console.log('[ReciterMusicLibrary] ⏭️ Skipping - no recitation at index', currentIndex);
      return;
    }
    
    console.log('[ReciterMusicLibrary] 📀 Loading track at index:', currentIndex);
    
    // Set fallback duration from recitation data if available
    const recitation = recitations[currentIndex];
    if (recitation.duration) {
      const parts = recitation.duration.split(':');
      let totalSeconds = 0;
      if (parts.length === 3) {
        totalSeconds = parseInt(parts[0]) * 3600 + parseInt(parts[1]) * 60 + parseInt(parts[2]);
      } else if (parts.length === 2) {
        totalSeconds = parseInt(parts[0]) * 60 + parseInt(parts[1]);
      }
      if (totalSeconds > 0) {
        setDuration(totalSeconds);
      }
    }
    
    // Always load and try to play when index changes
    loadAndPlayTrack(currentIndex);
    
  }, [currentIndex, loadAndPlayTrack, recitations]);
  
  // Increment listen count
  const incrementListenCount = async (recitationId) => {
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/recitations/${recitationId}/listen`, {
        method: 'POST'
      });
      console.log('[ReciterMusicLibrary] Listen count incremented');
    } catch (error) {
      console.error('[ReciterMusicLibrary] Error incrementing listen count:', error);
    }
  };
  
  // Format time
  const formatTime = (seconds) => {
    if (isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  // Progress bar click - FIXED: Simple seek without restart
  const handleProgressClick = useCallback((e) => {
    if (!audioRef.current || !duration) return;
    
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    const newTime = percent * duration;
    
    console.log('[ReciterMusicLibrary] 📍 Seeking to', newTime.toFixed(2), 'seconds');
    
    // Simply set currentTime - no need to pause/play
    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  }, [duration]);
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F6F1]">
        <div className="text-[#8B7355] text-xl">Yükleniyor...</div>
      </div>
    );
  }
  
  if (!reciter) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F6F1]">
        <div className="text-[#8B7355] text-xl">Hafız bulunamadı</div>
      </div>
    );
  }
  
  const currentRecitation = currentIndex >= 0 ? recitations[currentIndex] : null;
  
  return (
    <div className="min-h-screen bg-[#F9F6F1] p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Main Container */}
        <div className="grid grid-cols-1 lg:grid-cols-[350px_1fr] gap-6 bg-white rounded-2xl shadow-xl p-6 md:p-8 min-h-[700px]">
          
          {/* LEFT: Reciter Info */}
          <div className="bg-gradient-to-b from-[#8B7355] to-[#6B5744] rounded-2xl p-6 text-white flex flex-col">
            {/* Profile Image with Flag Badge */}
            <div className="relative w-48 h-48 mx-auto mb-6">
              <div className="w-full h-full rounded-full border-4 border-white/30 shadow-2xl overflow-hidden">
                {reciter.image ? (
                  <img 
                    src={reciter.image.startsWith('http') ? reciter.image : `${process.env.REACT_APP_BACKEND_URL}${reciter.image.replace('/uploads/', '/api/files/')}`}
                    alt={reciter.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      console.error('[ReciterMusicLibrary] Image load error:', reciter.image);
                      e.target.style.display = 'none';
                      e.target.nextElementSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div className="w-full h-full bg-gradient-to-br from-[#D5C4A1] to-[#8B7355] flex items-center justify-center text-6xl font-bold" style={{ display: reciter.image ? 'none' : 'flex' }}>
                  {reciter.name?.charAt(0)}
                </div>
              </div>
              {/* Flag badge - like online status */}
              {reciter.flagUrl && (
                <div className="absolute bottom-2 right-2 w-9 h-9 rounded-full border-2 border-white overflow-hidden shadow-lg">
                  <img 
                    src={reciter.flagUrl.startsWith('http') ? reciter.flagUrl : `${process.env.REACT_APP_BACKEND_URL}${reciter.flagUrl.replace('/uploads/', '/api/files/')}`} 
                    alt={reciter.country || 'Flag'} 
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
            </div>
            
            <h1 className="text-3xl font-bold text-center mb-2">{reciter.name}</h1>
            
            {/* Like Button - Compact & Theme Colored */}
            <div className="flex items-center justify-center gap-3 mb-6">
              <button
                onClick={handleLike}
                disabled={likeLoading}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm transition-all ${
                  hasLikedToday 
                    ? 'bg-[#D5C4A1] text-[#5D4E37]' 
                    : 'bg-white/15 text-white/80 hover:bg-white/25 hover:text-white'
                }`}
              >
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  viewBox="0 0 24 24" 
                  fill={hasLikedToday ? "#8B7355" : "none"}
                  stroke={hasLikedToday ? "#8B7355" : "currentColor"}
                  strokeWidth="2"
                  className={`w-4 h-4 ${likeLoading ? 'animate-pulse' : ''}`}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
                </svg>
                <span className="font-medium">{likeCount}</span>
              </button>
              <span className="text-xs text-white/50">
                {hasLikedToday ? 'Beğenildi' : 'Beğen'}
              </span>
            </div>
            
            {/* Stats */}
            <div className="bg-white/15 rounded-xl p-4 mb-6">
              <div className="flex justify-between items-center py-3 border-b border-white/10">
                <span className="text-sm text-white/80">Toplam Sure</span>
                <span className="font-bold">{recitations.length} Sure</span>
              </div>
              <div className="flex justify-between items-center py-3">
                <span className="text-sm text-white/80">Toplam Süre</span>
                <span className="font-bold">{getTotalDuration()}</span>
              </div>
            </div>
            
            {/* Control Buttons */}
            <div className="mt-auto space-y-3">
              <button 
                onClick={() => {
                  const newShuffle = !shuffle;
                  console.log('[ReciterMusicLibrary] 🔀 Shuffle toggled:', newShuffle);
                  setShuffle(newShuffle);
                  
                  // If turning shuffle ON and not currently playing, start from random track
                  if (newShuffle && recitations.length > 0 && currentIndex < 0) {
                    const randomIndex = Math.floor(Math.random() * recitations.length);
                    pendingPlayRef.current = true;
                    setCurrentIndex(randomIndex);
                  }
                }}
                className={`w-full py-3 px-4 rounded-xl font-bold transition-all flex items-center justify-center gap-2 ${
                  shuffle 
                    ? 'bg-white text-[#8B7355] shadow-lg' 
                    : 'bg-white/20 text-white border-2 border-white hover:bg-white hover:text-[#8B7355] hover:shadow-lg'
                }`}
              >
                <Shuffle className="w-5 h-5" />
                {shuffle ? 'Karışık Açık' : 'Tümünü Karıştır'}
              </button>
              <button 
                onClick={() => { 
                  console.log('[ReciterMusicLibrary] ▶️ Play all sequentially');
                  setShuffle(false);
                  pendingPlayRef.current = true;
                  setCurrentIndex(0);
                }}
                className="w-full py-3 px-4 rounded-xl font-bold bg-white/20 text-white border-2 border-white hover:bg-white hover:text-[#8B7355] transition-all flex items-center justify-center gap-2 hover:shadow-lg"
              >
                <Play className="w-5 h-5" />
                Sırayla Oynat
              </button>
            </div>
          </div>
          
          {/* RIGHT: Playlist */}
          <div className="flex flex-col h-full">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 pb-4 border-b-2 border-[#E8DCC8] mb-4">
              <h2 className="text-2xl text-[#5C4033]">
                <span className="font-bold">{reciter?.name}</span>
                <span className="font-normal text-xl text-[#8B7355] ml-2">Tüm Süreler ({recitations.length})</span>
              </h2>
              <div className="flex gap-2">
                <button 
                  onClick={() => setAutoNext(!autoNext)}
                  className={`w-11 h-11 rounded-xl border-2 flex items-center justify-center transition-all shadow-sm hover:shadow-md ${
                    autoNext 
                      ? 'bg-[#8B7355] text-white border-[#8B7355] shadow-md' 
                      : 'bg-white text-[#8B7355] border-[#D5C4A1] hover:bg-[#8B7355] hover:text-white'
                  }`}
                  title="Otomatik Geçiş"
                >
                  <SkipForward className="w-5 h-5" />
                </button>
                <button 
                  onClick={() => setRepeat(!repeat)}
                  className={`w-11 h-11 rounded-xl border-2 flex items-center justify-center transition-all shadow-sm hover:shadow-md ${
                    repeat 
                      ? 'bg-[#8B7355] text-white border-[#8B7355] shadow-md' 
                      : 'bg-white text-[#8B7355] border-[#D5C4A1] hover:bg-[#8B7355] hover:text-white'
                  }`}
                  title="Tekrarla"
                >
                  <Repeat className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            {/* Playlist - Max height for scroll */}
            <div className="flex-1 overflow-y-auto pr-2 space-y-2 max-h-[400px]">
              {recitations.map((recitation, index) => {
                const isCurrentTrack = index === currentIndex;
                
                return (
                  <div
                    key={recitation.id}
                    className={`grid grid-cols-[40px_1fr_80px_50px] gap-3 p-3 rounded-xl cursor-pointer transition-all border-2 ${
                      isCurrentTrack && currentIndex >= 0
                        ? 'bg-[#FFF4E6] border-[#8B7355]'
                        : 'border-transparent hover:bg-[#FFF9F0] hover:border-[#E8DCC8]'
                    }`}
                    onClick={() => playTrack(index)}
                  >
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center font-bold ${
                      isCurrentTrack && currentIndex >= 0 ? 'bg-[#8B7355] text-white' : 'bg-[#E8DCC8] text-[#5C4033]'
                    }`}>
                      {isCurrentTrack && isPlaying && currentIndex >= 0 ? '🔊' : index + 1}
                    </div>
                    
                    <div className="flex flex-col justify-center min-w-0">
                      <div className="font-bold text-[#3E3028] truncate">{getSurahName(recitation.surahId)}</div>
                      <div className="text-sm text-[#8B7355] truncate">
                        {getSurahArabic(recitation.surahId)} • {getSurahVerseCount(recitation.surahId)} Ayet
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-center text-sm text-[#8B7355]">
                      {recitation.duration || '-'}
                    </div>
                    
                    <div className="flex items-center justify-center">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (isCurrentTrack) {
                            togglePlay();
                          } else {
                            playTrack(index);
                          }
                        }}
                        className="w-10 h-10 rounded-full bg-gradient-to-br from-[#8B7355] to-[#6B5744] text-white flex items-center justify-center hover:scale-110 transition-all shadow-md hover:shadow-lg"
                      >
                        {isCurrentTrack && isPlaying ? (
                          <Pause className="w-4 h-4 ml-0.5" />
                        ) : (
                          <Play className="w-4 h-4 ml-0.5" />
                        )}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* Now Playing Bar */}
            {currentRecitation && (
              <div className="mt-4 p-5 bg-gradient-to-r from-[#8B7355] to-[#6B5744] rounded-2xl text-white">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-14 h-14 rounded-lg bg-white/20 flex items-center justify-center text-2xl font-bold">
                    {currentIndex + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold truncate">{getSurahName(currentRecitation.surahId)}</div>
                    <div className="text-sm text-white/80 truncate">
                      {reciter.name} • {getSurahVerseCount(currentRecitation.surahId)} Ayet
                    </div>
                  </div>
                </div>
                
                {/* Audio Error Warning */}
                {audioError && (
                  <div className="mb-4 p-3 bg-red-500/20 border border-red-300/30 rounded-lg text-center">
                    <p className="text-sm text-white font-medium">⚠️ {audioError}</p>
                    <p className="text-xs text-white/70 mt-1">Admin panelden MP3 formatında dosya yükleyiniz.</p>
                  </div>
                )}
                
                {/* Player Controls */}
                <div className="flex justify-center items-center gap-4 mb-4">
                  <button 
                    onClick={() => {
                      const newShuffle = !shuffle;
                      console.log('[ReciterMusicLibrary] 🔀 Shuffle toggled (mini):', newShuffle);
                      setShuffle(newShuffle);
                    }}
                    className={`w-10 h-10 rounded-full flex items-center justify-center transition-all shadow-lg hover:scale-110 ${
                      shuffle ? 'bg-white text-[#8B7355]' : 'bg-white/20 hover:bg-white/30'
                    }`}
                    title={shuffle ? 'Karışık Açık' : 'Karışık Çalma'}
                  >
                    <Shuffle className="w-4 h-4" />
                  </button>
                  <button 
                    onClick={previousTrack}
                    className="w-11 h-11 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-all shadow-lg hover:scale-110"
                    title="Önceki Sure"
                  >
                    <SkipBack className="w-5 h-5" />
                  </button>
                  <button 
                    onClick={togglePlay}
                    className="w-16 h-16 rounded-full bg-white text-[#8B7355] flex items-center justify-center hover:scale-110 transition-all shadow-2xl hover:shadow-3xl"
                    title={isPlaying ? 'Duraklat' : 'Oynat'}
                  >
                    {isPlaying ? (
                      <Pause className="w-7 h-7" />
                    ) : (
                      <Play className="w-7 h-7 ml-1" />
                    )}
                  </button>
                  <button 
                    onClick={() => {
                      console.log('[ReciterMusicLibrary] ⏭️ Skip Forward button clicked!');
                      nextTrack();
                    }}
                    className="w-11 h-11 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-all shadow-lg hover:scale-110"
                    title="Sonraki Sure"
                  >
                    <SkipForward className="w-5 h-5" />
                  </button>
                  <button 
                    onClick={() => setRepeat(!repeat)}
                    className={`w-10 h-10 rounded-full flex items-center justify-center transition-all shadow-lg hover:scale-110 ${
                      repeat ? 'bg-white text-[#8B7355]' : 'bg-white/20 hover:bg-white/30'
                    }`}
                    title={repeat ? 'Tekrar Açık' : 'Tekrarla'}
                  >
                    <Repeat className="w-4 h-4" />
                  </button>
                </div>
                
                {/* Progress Bar */}
                <div className="flex items-center gap-3">
                  <span className="text-xs text-white/80">{formatTime(currentTime)}</span>
                  <div 
                    className="flex-1 h-2 bg-white/30 rounded-full cursor-pointer"
                    onClick={handleProgressClick}
                  >
                    <div 
                      className="h-full bg-white rounded-full transition-all"
                      style={{ width: `${(currentTime / duration) * 100}%` }}
                    />
                  </div>
                  <span className="text-xs text-white/80">{formatTime(duration)}</span>
                </div>
              </div>
            )}
          </div>
        </div>
        
        {/* Hidden Audio Element with crossorigin for CORS */}
        <audio ref={audioRef} preload="auto" crossOrigin="anonymous" />
      </div>
    </div>
  );
};

export default ReciterMusicLibrary;
